import type React from "react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import "./style.css";
import { AllowUpIcon } from "~/components/icons/AllowUpIcon";

interface Props {
	title: string;
	children: ReactNode;
	show: boolean;
	onShow: () => void;
	disable?: boolean;
}

export const AccordionMenu: React.FC<Props> = ({
	title,
	children,
	show,
	onShow,
	disable,
}) => {
	const childElement = useRef<HTMLDivElement>(null);
	const [showChildren, setShowChildren] = useState(show);
	const [childHeight, setChildHeight] = useState(0);
	const [reverseIcon, setReverseIcon] = useState(!show);

	useEffect(() => {
		setShowChildren(show);
		setReverseIcon(!show);
	}, [show]);

	useEffect(() => {
		if (!childElement.current) return;

		const initialHeight = childElement.current.clientHeight;
		setChildHeight(initialHeight);

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const newHeight = entry.target.clientHeight;
				setChildHeight(newHeight);
			}
		});

		resizeObserver.observe(childElement.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, []); // Set it up once, and the observer will automatically detect changes.

	if (disable) {
		return (
			<div className="t4-accordion-item t4-accordion-item-disabled cursor-not-allowed">
				{title}
				<div
					className={`t4-accordion-icon ${
						reverseIcon && "t4-accordion-reverse"
					}`}
				>
					<AllowUpIcon color={{ dark: "white", light: "black" }} />
				</div>
			</div>
		);
	}

	const handleClick = () => {
		if (!showChildren) onShow();
		if (childElement.current) {
			setShowChildren(!showChildren);
		}
		setReverseIcon(!reverseIcon);
	};

	return (
		<>
			<button
				type="button"
				onClick={handleClick}
				className="t4-accordion-item cursor-pointer w-full text-left"
			>
				{title}
				<div
					className={`t4-accordion-icon ${
						reverseIcon && "t4-accordion-reverse"
					}`}
				>
					<AllowUpIcon color={{ dark: "white", light: "black" }} />
				</div>
			</button>
			<div
				className="t4-accordion-child-item"
				style={{
					height:
						children && showChildren ? `calc(${childHeight}px + 3rem)` : "0px",
					opacity: children && showChildren ? 1 : 0,
				}}
			>
				<div ref={childElement}>{children}</div>
			</div>
		</>
	);
};
